import { useEffect, useState } from 'react';
import { admin as adminApi, ApiError } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { AdminGuard } from '@/components/AdminGuard';
import { AdminSubNav } from '@/components/AdminSubNav';
import {
  PixelVerificationDialog,
  type PixelVerificationResult,
} from '@/components/PixelVerificationDialog';

/**
 * /app/admin/platform-pixel — manage Poof's platform-level Meta CAPI pixel.
 *
 * Wrapped in <AdminGuard>; defense in depth on the server with
 * requireAdmin in /api/admin/platform-pixel.
 *
 * The `enabled` toggle is the kill switch — flipping it OFF stops the
 * platform pixel from firing on any click platform-wide without a deploy.
 * Per-customer pixels (Pro/Agency feature) are unaffected.
 *
 * Access token UX: the server never returns the plaintext, only `hasToken`.
 * When one is stored, the input renders a "•••• set" placeholder so the
 * admin can save other fields (or toggle Enabled) without re-pasting it.
 * Typing a new value replaces the stored one; clearing the field to empty
 * sends `accessToken: ""` which the server reads as "clear it".
 */
export default function AdminPlatformPixel() {
  return (
    <AdminGuard>
      <AdminPlatformPixelContent />
    </AdminGuard>
  );
}

function AdminPlatformPixelContent() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [tokenTouched, setTokenTouched] = useState(false);
  const [hasStoredToken, setHasStoredToken] = useState(false);
  const [testEventCode, setTestEventCode] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Verification modal — opens on save outcome (success OR Meta rejection).
  // Other errors (network, validation 400s without metaError) keep using
  // the inline `err` banner above the form.
  const [verifyResult, setVerifyResult] = useState<PixelVerificationResult | null>(null);

  useEffect(() => {
    adminApi.platformPixel
      .get()
      .then((r) => {
        setPixelId(r.pixelId || '');
        setTestEventCode(r.testEventCode || '');
        setEnabled(Boolean(r.enabled));
        setHasStoredToken(Boolean(r.hasToken));
        setUpdatedAt(r.updatedAt);
      })
      .catch((e) =>
        setErr((e as Error).message || 'Failed to load platform pixel config.'),
      )
      .finally(() => setLoading(false));
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    setVerifyResult(null);
    try {
      const body: {
        pixelId: string | null;
        testEventCode: string | null;
        enabled: boolean;
        accessToken?: string | null;
      } = {
        pixelId: pixelId.trim() || null,
        testEventCode: testEventCode.trim() || null,
        enabled,
      };
      if (tokenTouched) {
        body.accessToken = accessToken;
      }
      await adminApi.platformPixel.put(body);
      setSaved(true);
      if (tokenTouched && accessToken) {
        setHasStoredToken(true);
        setAccessToken('');
        setTokenTouched(false);
        // Token was actually set/changed → show the verified-modal so the
        // admin SEES Meta accepted it. Skip the modal when only fields
        // other than the token changed (no validation happened server-side).
        setVerifyResult({ ok: true });
      } else if (tokenTouched && !accessToken) {
        setHasStoredToken(false);
        setTokenTouched(false);
      }
      setUpdatedAt(Date.now() / 1000);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'pixel_validation_failed') {
        const raw = (e.raw || {}) as {
          metaError?: string;
          metaCode?: number;
          httpStatus?: number;
        };
        setVerifyResult({
          ok: false,
          metaError: raw.metaError,
          metaCode: raw.metaCode,
          httpStatus: raw.httpStatus,
        });
      } else {
        const msg = (e as Error).message || '';
        if (msg.includes('pixel_id_and_token_required_to_enable')) {
          setErr(
            'You need both a pixel ID and an access token set before you can enable the platform pixel.',
          );
        } else if (msg.includes('invalid_pixel_id')) {
          setErr('Pixel ID must be a numeric string (15-16 digits).');
        } else if (msg.includes('encryption_key_missing')) {
          setErr('Server misconfigured: POOF_ENCRYPTION_KEY is not set.');
        } else if (msg.includes('pixel_id_required_for_token_validation')) {
          setErr('Paste a pixel ID alongside the new token so we can verify it.');
        } else {
          setErr(msg || 'Save failed. Try again.');
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <DashboardLayout title="Admin · Platform Pixel">
      <AdminSubNav />

      {err && (
        <p className="mt-2 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {err}
        </p>
      )}

      {!loading && (
        <div className="mt-2 max-w-2xl space-y-5">
          {/* Explainer card — what this controls + the kill switch story. */}
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-glass)] p-4 text-sm">
            <div className="font-semibold">Platform Meta Pixel (CAPI)</div>
            <p className="mt-1 text-[13px] text-[var(--color-muted-foreground)]">
              Fires a server-side <code>PageView</code> Conversions API event
              to <em>our</em> pixel for every legitimate click across every
              customer's links — builds the platform-wide audience.
              Decoy/bot traffic never fires. EU traffic is sent with{' '}
              <code>LDU</code> automatically.
            </p>
            <p className="mt-2 text-[13px] text-[var(--color-muted-foreground)]">
              The <strong>Enabled</strong> toggle is a global kill switch.
              Flip it off to stop firing platform-wide without a redeploy.
              Per-customer pixels (Pro/Agency feature) are unaffected by
              this toggle.
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4 rounded-xl border border-[var(--color-border)] p-4">
            <Field
              label="Pixel ID"
              hint="The numeric Pixel ID from Meta Events Manager."
            >
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={pixelId}
                onChange={(e) => setPixelId(e.target.value)}
                placeholder="e.g. 681153731669782"
                className={inputCls}
              />
            </Field>

            <Field
              label="Access token (Conversions API)"
              hint={
                hasStoredToken && !tokenTouched
                  ? 'A token is already stored. Leave this empty to keep it; type a new value to replace it.'
                  : 'Generate at Meta Events Manager → your pixel → Settings → Conversions API → Generate access token.'
              }
            >
              <input
                type="password"
                value={accessToken}
                onChange={(e) => {
                  setAccessToken(e.target.value);
                  setTokenTouched(true);
                }}
                placeholder={
                  hasStoredToken && !tokenTouched
                    ? '•••• already set'
                    : 'Paste the EAA… token here'
                }
                autoComplete="new-password"
                className={inputCls}
              />
              {hasStoredToken && tokenTouched && accessToken === '' && (
                <p className="mt-1 text-[11px] text-[var(--color-destructive)]">
                  Saving with this field empty will REMOVE the stored token.
                </p>
              )}
            </Field>

            <Field
              label="Test event code (optional)"
              hint="When set, events are tagged so they appear in Meta's Test Events tab in real time. Leave blank for production traffic."
            >
              <input
                type="text"
                value={testEventCode}
                onChange={(e) => setTestEventCode(e.target.value)}
                placeholder="TEST12345"
                className={inputCls}
              />
            </Field>

            {/* Enabled toggle — the kill switch. */}
            <label className="flex items-start gap-3 rounded-lg border border-[var(--color-border)] p-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium">
                  Enabled
                </span>
                <span className="block text-[12px] text-[var(--color-muted-foreground)]">
                  When OFF, the platform pixel does not fire on any click,
                  ever. Per-customer pixels keep firing.
                </span>
              </span>
            </label>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-50"
              >
                {busy
                  ? tokenTouched && accessToken
                    ? 'Verifying with Meta…'
                    : 'Saving…'
                  : 'Save'}
              </button>
              {saved && (
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  Saved.
                </span>
              )}
              {updatedAt && (
                <span className="ml-auto text-[11px] text-[var(--color-muted-foreground)]">
                  Last updated{' '}
                  {new Date(updatedAt * 1000).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <PixelVerificationDialog
        open={verifyResult !== null}
        result={verifyResult}
        onClose={() => setVerifyResult(null)}
      />
    </DashboardLayout>
  );
}

const inputCls =
  'mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-[11px] text-[var(--color-muted-foreground)]">
          {hint}
        </span>
      )}
    </label>
  );
}
